import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Send,
  Smile,
  Paperclip,
  Users,
  MessageCircle,
  Phone,
  Video,
  MoreVertical,
  ArrowLeft,
  Settings,
  Search,
  Check,
  CheckCheck,
  AlertCircle,
  Wifi,
  WifiOff,
  Download,
  Monitor,
  PhoneOff,
  Trash2
} from 'lucide-react'
import { useP2PStore } from '../../store/p2pStore'
import { EmojiPicker } from '../ui/EmojiPicker'
import { FileTransferUI } from '../ui/FileTransferUI'
import { VideoCallUI } from '../ui/VideoCallUI'
import { DesktopCallButton } from '../ui/DesktopCallButton'
import { MobileAudioGuidance } from '../ui/MobileAudioGuidance'
import { LocationSharingButton } from '../ui/LocationSharingButton'
import { LocationMessage } from '../ui/LocationMessage'
import { CallState } from '../../services/MediaService'
import { toast, TOAST_GROUPS } from '../../utils/toastManager'
import { locationService } from '../../services/LocationService'

// File transfer tracking
interface FileTransferData {
  id: string
  name: string
  size: number
  type: string
  status: 'pending' | 'uploading' | 'downloading' | 'completed' | 'failed'
  progress: number
  speed?: number
  timeRemaining?: number
  error?: string
  sender?: string
  timestamp: Date
}

export function EnhancedChatPage() {
  const navigate = useNavigate()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messageInputRef = useRef<HTMLInputElement>(null)

  // State
  const [messageText, setMessageText] = useState('')
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [showFileTransfer, setShowFileTransfer] = useState(false)
  const [selectedPeer, setSelectedPeer] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [showMoreDropdown, setShowMoreDropdown] = useState(false)

  // Call states
  const [callState, setCallState] = useState<CallState>({
    isInCall: false,
    callType: null,
    localStream: null,
    remoteStream: null,
    activePeer: null,
    activePeerAlias: null,
    activePeerAvatar: null,
    callStartTime: null,
    callProgress: 'idle',
    callError: null
  })
  // Incoming call state moved to MainLayout for global visibility
  const [isMuted, setIsMuted] = useState(false)
  const [isVideoEnabled, setIsVideoEnabled] = useState(true)
  const [isScreenSharing, setIsScreenSharing] = useState(false)
  const [videoQuality, setVideoQuality] = useState<'excellent' | 'good' | 'poor' | 'failed'>('excellent')
  const [isDegraded, setIsDegraded] = useState(false)

  // Mobile audio guidance state
  const [showMobileAudioGuidance, setShowMobileAudioGuidance] = useState(false)

  // File transfer states
  const [activeTransfers, setActiveTransfers] = useState<FileTransferData[]>([])

  // Direct store access for maximum stability (avoid multiple hook calls)
  const store = useP2PStore()
  const {
    messages,
    connections,
    messageService,
    mediaService,
    localSelfta,
    connectionManager,
    selectedPeerForChat,
    setSelectedPeerForChat,
    isOnline,
    isInitializing,
    isNetworkStabilized,
    connectionProgress,
    networkError,
    clearMessages
  } = store

  // Convert connections Map to Array and derive states
  const connectionsArray = Array.from(connections.values())
  const connectionStatus = {
    isOnline,
    isInitializing,
    isNetworkStabilized,
    connectionProgress,
    connectionCount: connectionsArray.length,
    error: networkError
  }

  const typingPeers = connectionsArray
    .filter(peer => peer.isTyping)
    .map(peer => ({ id: peer.id, alias: peer.alias }))

  // Subscribe to media service events
  useEffect(() => {
    if (!mediaService) return

    const handleCallStateChange = (newState: CallState) => {
      console.log('📞 Call state changed:', newState)
      // AVATAR DEBUG: Check state change data
      console.log(`🖼️ AVATAR DEBUG - handleCallStateChange:`)
      console.log(`  newState.activePeerAvatar: "${newState.activePeerAvatar}" (${typeof newState.activePeerAvatar})`)
      console.log(`  newState.activePeerAvatar === null: ${newState.activePeerAvatar === null}`)
      console.log(`  newState.activePeerAvatar === 'null': ${newState.activePeerAvatar === 'null'}`)

      // CRITICAL FIX: Clean avatar data in state changes
      const cleanedState = {
        ...newState,
        activePeerAvatar: (newState.activePeerAvatar === "null" || newState.activePeerAvatar === null) ? null : newState.activePeerAvatar
      }
      console.log(`🖼️ AVATAR DEBUG - cleanedState.activePeerAvatar: "${cleanedState.activePeerAvatar}"`)
      setCallState(cleanedState)
    }

    // Incoming call handling moved to MainLayout for global visibility

    const handleRemoteStream = (eventData: any) => {
      const remoteStream = eventData?.data || eventData
      console.log('📞 Remote stream received:', remoteStream)
      setCallState(prev => ({
        ...prev,
        remoteStream
      }))

      // MOBILE FIX: Show audio guidance for mobile users on voice calls
      const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      if (isMobile && callState.callType === 'voice' && callState.callProgress === 'connected') {
        // Show guidance after a short delay to let call establish
        setTimeout(() => {
          setShowMobileAudioGuidance(true)
        }, 2000)
      }
    }

    // Voice-First Architecture: Handle local video stream for upgrades
    const handleLocalVideoStream = (eventData: any) => {
      const localStream = eventData?.data || eventData
      console.log('🎯 Voice-First: Local video stream received:', localStream)
      setCallState(prev => ({
        ...prev,
        localStream
      }))
    }

    const unsubscribeState = mediaService.onCallStateChange(handleCallStateChange)
    // IncomingCall events now handled globally in MainLayout
    mediaService.on('remoteStream', handleRemoteStream)
    mediaService.on('localVideoStream', handleLocalVideoStream)

    return () => {
      unsubscribeState()
      // IncomingCall listeners removed from EnhancedChatPage (now in MainLayout)
      mediaService.removeAllListeners('remoteStream')
      mediaService.removeAllListeners('localVideoStream')
    }
  }, [mediaService])

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Set selected peer from store on mount
  useEffect(() => {
    if (selectedPeerForChat && !selectedPeer) {
      setSelectedPeer(selectedPeerForChat)
    }
  }, [selectedPeerForChat, selectedPeer])

  // Focus message input when peer is selected
  useEffect(() => {
    if (selectedPeer && messageInputRef.current && !callState.isInCall) {
      setTimeout(() => messageInputRef.current?.focus(), 100)
    }
  }, [selectedPeer, callState.isInCall])

  // Filter messages for selected peer
  const filteredMessages = useMemo(() => {
    if (!selectedPeer || !localSelfta) return []

    return messages.filter(msg => {
      const isReceivedFromPeer = msg.senderId === selectedPeer && !msg.isSelf
      const isSentToPeer = msg.senderId === localSelfta.id && msg.recipients.includes(selectedPeer)
      const isSentMessage = msg.isSelf && msg.recipients.includes(selectedPeer)
      return isReceivedFromPeer || isSentToPeer || isSentMessage
    }).sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
  }, [messages, selectedPeer, localSelfta])

  // Connected peers
  const connectedPeers = connectionsArray.filter(conn => conn.connectionState === 'connected')

  // Filter peers for search
  const filteredPeers = connectedPeers.filter(peer =>
    !searchTerm ||
    peer.alias?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    peer.displayName?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Get selected peer data
  const selectedPeerData = connectionsArray.find(conn => conn.id === selectedPeer)

  // Voice call handler
  const handleVoiceCall = useCallback(async () => {
    if (!selectedPeer || !selectedPeerData || !mediaService || !localSelfta) {
      toast.error('Cannot start call: Not connected', TOAST_GROUPS.CALL)
      return
    }

    try {
      // CRITICAL DEBUG: Check what alias data we're actually sending
      console.log(`🚨 ENHANCED CHAT PAGE DEBUG - Voice call initiation:`)
      console.log(`  selectedPeer: "${selectedPeer}"`)
      console.log(`  selectedPeerData.selfta?.alias: "${selectedPeerData.selfta?.alias}"`)
      console.log(`  selectedPeerData.alias: "${selectedPeerData.alias}"`)
      console.log(`  localSelfta.id: "${localSelfta.id}"`)
      console.log(`  localSelfta.alias: "${localSelfta.alias}" ← THIS IS THE fromAlias!`)
      console.log(`  localSelfta full object:`, localSelfta)

      // AVATAR DEBUG: Check what avatar we're about to pass for voice call
      const rawAvatar = selectedPeerData.selfta?.profileImage
      // CRITICAL FIX: Clean string "null" to actual null
      const targetAvatar = (rawAvatar === "null" || rawAvatar === null || rawAvatar === undefined || rawAvatar === "") ? null : rawAvatar
      console.log(`🖼️ AVATAR DEBUG - EnhancedChatPage voice call:`)
      console.log(`  selectedPeerData.selfta:`, selectedPeerData.selfta)
      console.log(`  selectedPeerData.selfta?.profileImage:`, selectedPeerData.selfta?.profileImage)
      console.log(`  targetAvatar being passed:`, targetAvatar)
      console.log(`  targetAvatar === null:`, targetAvatar === null)
      console.log(`  targetAvatar === 'null':`, targetAvatar === 'null')

      const success = await mediaService.initiateVoiceCall(
        selectedPeer,
        selectedPeerData.selfta?.alias || selectedPeerData.alias || `User_${selectedPeerData.id?.slice(-8) || 'Unknown'}`,
        localSelfta.id,
        localSelfta.alias,
        selectedPeerData.connection!,
        targetAvatar
      )

      if (success) {
        const currentState = mediaService.getCallState()
        setCallState({
          isInCall: true,
          callType: 'voice',
          localStream: currentState.localStream,
          remoteStream: currentState.remoteStream, // May be null initially
          activePeer: selectedPeer,
          activePeerAlias: selectedPeerData.alias || 'Unknown',
          activePeerAvatar: (currentState.activePeerAvatar === "null" || currentState.activePeerAvatar === null) ? null : currentState.activePeerAvatar, // Clean avatar data
          callStartTime: new Date(),
          callProgress: currentState.callProgress || 'idle',
          callError: currentState.callError || null
        })
        console.log('📞 Voice call initiated successfully')
      }
    } catch (error) {
      console.error('Voice call failed:', error)
      toast.error('Failed to start voice call', TOAST_GROUPS.CALL)
    }
  }, [selectedPeer, selectedPeerData, mediaService, localSelfta])

  // Video call handler
  const handleVideoCall = useCallback(async () => {
    if (!selectedPeer || !selectedPeerData || !mediaService || !localSelfta) {
      toast.error('Cannot start call: Not connected', TOAST_GROUPS.CALL)
      return
    }

    try {
      // AVATAR DEBUG: Check what avatar we're about to pass for video call
      const rawAvatar = selectedPeerData.selfta?.profileImage
      // CRITICAL FIX: Clean string "null" to actual null
      const targetAvatar = (rawAvatar === "null" || rawAvatar === null || rawAvatar === undefined || rawAvatar === "") ? null : rawAvatar
      console.log(`🖼️ AVATAR DEBUG - EnhancedChatPage video call:`)
      console.log(`  selectedPeerData.selfta:`, selectedPeerData.selfta)
      console.log(`  selectedPeerData.selfta?.profileImage:`, selectedPeerData.selfta?.profileImage)
      console.log(`  targetAvatar being passed:`, targetAvatar)

      const success = await mediaService.initiateVideoCall(
        selectedPeer,
        selectedPeerData.selfta?.alias || selectedPeerData.alias || `User_${selectedPeerData.id?.slice(-8) || 'Unknown'}`,
        localSelfta.id,
        localSelfta.alias,
        selectedPeerData.connection!,
        undefined, // localVideoElement
        targetAvatar
      )

      if (success) {
        const currentState = mediaService.getCallState()
        setCallState({
          isInCall: true,
          callType: 'video',
          localStream: currentState.localStream,
          remoteStream: currentState.remoteStream, // May be null initially
          activePeer: selectedPeer,
          activePeerAlias: selectedPeerData.alias || 'Unknown',
          activePeerAvatar: (currentState.activePeerAvatar === "null" || currentState.activePeerAvatar === null) ? null : currentState.activePeerAvatar, // Clean avatar data
          callStartTime: new Date(),
          callProgress: currentState.callProgress || 'idle',
          callError: currentState.callError || null
        })
        setIsVideoEnabled(true)
        console.log('📹 Video call initiated successfully')
      }
    } catch (error) {
      console.error('Video call failed:', error)
      toast.error('Failed to start video call', TOAST_GROUPS.CALL)
    }
  }, [selectedPeer, selectedPeerData, mediaService, localSelfta])

  // Accept/Decline call handlers moved to MainLayout for global accessibility

  // End call
  const handleEndCall = useCallback(() => {
    if (!mediaService) return

    const peerConnection = connectionsArray.find(c => c.id === callState.activePeer)
    mediaService.endCall(peerConnection?.connection)

    setCallState({
      isInCall: false,
      callType: null,
      localStream: null,
      remoteStream: null,
      activePeer: null,
      activePeerAlias: null,
      activePeerAvatar: null,
      callStartTime: null,
      callProgress: 'idle',
      callError: null
    })
    setIsMuted(false)
    setIsVideoEnabled(true)
    setIsScreenSharing(false)
  }, [mediaService, callState.activePeer, connections])

  // Voice-First Architecture: Handle video quality upgrade
  const handleUpgradeToVideo = useCallback(async () => {
    if (!mediaService) {
      toast.error('Media service not available', TOAST_GROUPS.CALL)
      return
    }

    // Check if we're in a voice call that can be upgraded
    if (callState.callType === 'voice' && callState.isInCall) {
      try {
        console.log('🎯 Voice-First: Upgrading voice call to video')
        const success = await mediaService.upgradeVoiceCallToVideo()

        if (success) {
          setVideoQuality('excellent')
          setIsDegraded(false)
          console.log('✅ Voice-First: Successfully upgraded to video call')
        }
        return
      } catch (error) {
        console.error('❌ Voice-First: Video upgrade failed:', error)
        toast.error('Failed to enable video', TOAST_GROUPS.CALL)
        return
      }
    } else if (!selectedPeer || !selectedPeerData?.connection?.open) {
      toast.error('No active connection for video upgrade', TOAST_GROUPS.CALL)
      return
    } else {
      // Fallback to original video call initiation
      try {
        console.log('🔄 Attempting to initiate new video call')
        const success = await mediaService.initiateVideoCall(
          selectedPeer,
          selectedPeerData.alias || 'Unknown',
          mediaService.getPeerId() || '',
          mediaService.getAlias() || '',
          selectedPeerData.connection
        )

        if (success) {
          setVideoQuality('excellent')
          setIsDegraded(false)
          console.log('✅ Successfully initiated video call')
        }
      } catch (error) {
        console.error('❌ Video call initiation failed:', error)
        toast.error('Failed to start video call', TOAST_GROUPS.CALL)
      }
    }
  }, [mediaService, callState.callType, callState.isInCall, selectedPeer, selectedPeerData])

  // Toggle mute
  const handleToggleMute = useCallback(() => {
    if (callState.localStream) {
      const audioTracks = callState.localStream.getAudioTracks()
      audioTracks.forEach(track => {
        track.enabled = isMuted
      })
      setIsMuted(!isMuted)
    }
  }, [callState.localStream, isMuted])

  // Toggle video
  const handleToggleVideo = useCallback(() => {
    if (callState.localStream && callState.callType === 'video') {
      const videoTracks = callState.localStream.getVideoTracks()
      videoTracks.forEach(track => {
        track.enabled = !isVideoEnabled
      })
      setIsVideoEnabled(!isVideoEnabled)
    }
  }, [callState.localStream, callState.callType, isVideoEnabled])

  // Monitor video quality (basic implementation)
  useEffect(() => {
    if (callState.callType === 'video' && callState.remoteStream) {
      const videoTrack = callState.remoteStream.getVideoTracks()[0]
      if (!videoTrack) {
        setVideoQuality('failed')
        setIsDegraded(true)
        return
      }

      // Simple quality check based on track state
      const checkQuality = () => {
        if (videoTrack.readyState === 'ended') {
          setVideoQuality('failed')
          setIsDegraded(true)
        } else if (videoTrack.enabled === false) {
          setVideoQuality('poor')
          setIsDegraded(true)
        } else {
          setVideoQuality('excellent')
          setIsDegraded(false)
        }
      }

      checkQuality()
      const interval = setInterval(checkQuality, 2000) // Check every 2 seconds

      return () => clearInterval(interval)
    } else {
      // Reset quality for non-video calls
      setVideoQuality('excellent')
      setIsDegraded(false)
      return () => {} // Empty cleanup function for consistency
    }
  }, [callState.callType, callState.remoteStream])

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element
      if (showMoreDropdown && !target.closest('[data-dropdown="more-options"]')) {
        setShowMoreDropdown(false)
      }
    }

    if (showMoreDropdown) {
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside)
      }, 0)
    }

    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [showMoreDropdown])

  // Toggle screen share
  const handleToggleScreen = useCallback(async () => {
    if (!mediaService || !selectedPeerData || !localSelfta) return

    if (isScreenSharing) {
      mediaService.stopScreenShare()
      setIsScreenSharing(false)
    } else {
      try {
        const success = await mediaService.startScreenShare(
          selectedPeerData.id,
          selectedPeerData.selfta?.alias || selectedPeerData.alias || `User_${selectedPeerData.id?.slice(-8) || 'Unknown'}`,
          localSelfta.id,
          localSelfta.alias,
          selectedPeerData.connection!
        )
        if (success) {
          setIsScreenSharing(true)
        }
      } catch (error) {
        console.error('Screen share failed:', error)
        toast.error('Failed to share screen', TOAST_GROUPS.CALL)
      }
    }
  }, [mediaService, selectedPeerData, localSelfta, isScreenSharing])

  // Clear chat history
  const handleClearChat = useCallback(() => {
    if (window.confirm('Are you sure you want to clear all chat history? This cannot be undone.')) {
      clearMessages()
      setShowMoreDropdown(false)
      // Note: Store already shows toast notification
    }
  }, [clearMessages])

  // Send message
  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedPeer || !localSelfta || !messageService || !connectionManager) {
      return
    }

    const connectionData = connectionsArray.find(conn => conn.id === selectedPeer)
    if (!connectionData?.connection) {
      toast.error('Peer connection not found', TOAST_GROUPS.CONNECTION)
      return
    }

    try {
      const success = await messageService.sendMessage(
        messageText.trim(),
        selectedPeer,
        localSelfta.id,
        localSelfta.alias,
        connectionData.connection
      )

      if (success) {
        setMessageText('')
      } else {
        toast.error('Failed to send message', TOAST_GROUPS.GENERAL)
      }
    } catch (error) {
      console.error('Error sending message:', error)
      toast.error('Failed to send message')
    }
  }

  // Handle key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  // Handle emoji selection
  const handleEmojiSelect = (emoji: string) => {
    setMessageText(prev => prev + emoji)
    setShowEmojiPicker(false)
    setTimeout(() => {
      messageInputRef.current?.focus()
    }, 0)
  }

  // Handle file selection
  const handleFileSelect = async (files: File[]) => {
    if (!selectedPeer || !localSelfta || !messageService) {
      toast.error('Cannot send file: Not connected', TOAST_GROUPS.FILE_TRANSFER)
      return
    }

    const connectionData = connectionsArray.find(conn => conn.id === selectedPeer)
    if (!connectionData?.connection) {
      toast.error('Peer connection not found', TOAST_GROUPS.CONNECTION)
      return
    }

    // Create transfer entries
    const newTransfers: FileTransferData[] = files.map(file => ({
      id: `${Date.now()}-${Math.random()}`,
      name: file.name,
      size: file.size,
      type: file.type,
      status: 'uploading',
      progress: 0,
      timestamp: new Date()
    }))

    setActiveTransfers(prev => [...prev, ...newTransfers])

    // Send each file
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      const transfer = newTransfers[i]

      if (!file || !transfer) {
        console.error('File or transfer is undefined', { file, transfer })
        continue
      }

      try {
        // Simulate progress updates
        const progressInterval = setInterval(() => {
          setActiveTransfers(prev =>
            prev.map(t =>
              t.id === transfer.id
                ? { ...t, progress: Math.min(t.progress + 10, 90) }
                : t
            )
          )
        }, 200)

        await messageService.sendFile(
          file,
          selectedPeer,
          localSelfta.id,
          localSelfta.alias,
          connectionData.connection
        )

        clearInterval(progressInterval)

        // Mark as completed
        setActiveTransfers(prev =>
          prev.map(t =>
            t.id === transfer.id
              ? { ...t, status: 'completed', progress: 100 }
              : t
          )
        )

        toast.success(`${file.name} sent successfully!`, TOAST_GROUPS.FILE_TRANSFER)
      } catch (error) {
        console.error('Error sending file:', error)
        setActiveTransfers(prev =>
          prev.map(t =>
            t.id === transfer.id
              ? { ...t, status: 'failed', error: 'Transfer failed' }
              : t
          )
        )
        toast.error(`Failed to send ${file.name}`, TOAST_GROUPS.FILE_TRANSFER)
      }
    }
  }

  // Handle file download
  const handleFileDownload = (fileData: { name: string; data: string; mimeType: string }) => {
    try {
      const link = document.createElement('a')
      link.href = fileData.data
      link.download = fileData.name
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      toast.success(`Downloaded ${fileData.name}`, TOAST_GROUPS.FILE_TRANSFER)
    } catch (error) {
      console.error('Error downloading file:', error)
      toast.error('Failed to download file', TOAST_GROUPS.FILE_TRANSFER)
    }
  }

  // Handle location sharing
  const handleLocationShare = async (
    location: any,
    shareType: 'one-time' | 'live' | 'duration',
    duration?: number
  ) => {
    if (!selectedPeerForChat || !localSelfta || !messageService) {
      toast.error('Cannot share location: Not connected', TOAST_GROUPS.CONNECTION)
      return
    }

    const connectionData = connectionsArray.find(conn => conn.id === selectedPeerForChat)
    if (!connectionData?.connection) {
      toast.error('Peer connection not found', TOAST_GROUPS.CONNECTION)
      return
    }

    try {
      if (shareType === 'one-time' && location) {
        // Send one-time location
        await messageService.sendLocationMessage(
          location,
          shareType,
          selectedPeerForChat,
          localSelfta.id,
          localSelfta.alias,
          connectionData.connection
        )
      } else if (shareType === 'live' || shareType === 'duration') {
        // Set up live location tracking with message service
        const handleLiveLocationUpdate = async (event: any) => {
          const data = event.data || event
          if (data.peerId === selectedPeerForChat) {
            await messageService.sendLocationMessage(
              data.location,
              shareType,
              selectedPeerForChat,
              localSelfta.id,
              localSelfta.alias,
              connectionData.connection,
              duration
            )
          }
        }

        locationService.on('live-location-update', handleLiveLocationUpdate)

        // Clean up when sharing ends
        const handleTrackingEnd = () => {
          locationService.off('live-location-update', handleLiveLocationUpdate)
        }

        if (shareType === 'duration') {
          locationService.on('duration-sharing-ended', handleTrackingEnd)
        } else {
          locationService.on('tracking-stopped', handleTrackingEnd)
        }
      }
    } catch (error) {
      console.error('Error sharing location:', error)
      toast.error('Failed to share location', TOAST_GROUPS.CONNECTION)
    }
  }

  // Handle peer selection
  const handlePeerSelect = (peerId: string) => {
    setSelectedPeer(peerId)
    setSelectedPeerForChat(peerId)
    setSearchTerm('')
  }

  // Format message timestamp
  const formatMessageTime = (timestamp: Date) => {
    const now = new Date()
    const diff = now.getTime() - timestamp.getTime()

    if (diff < 60000) {
      return 'Just now'
    } else if (diff < 3600000) {
      return `${Math.floor(diff / 60000)}m ago`
    } else if (diff < 86400000) {
      return timestamp.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    } else {
      return timestamp.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    }
  }

  // Get typing indicator text
  const getTypingText = () => {
    const typing = typingPeers.filter(peer => peer.id === selectedPeer)
    if (typing.length === 0) return null
    return `${typing[0]?.alias || 'User'} is typing...`
  }

  if (!connectionStatus.isOnline) {
    return (
      <div className="p-6 flex flex-col items-center justify-center h-full">
        <WifiOff className="w-16 h-16 text-secondary-300 mb-4" />
        <h2 className="text-xl font-semibold text-secondary-900 mb-2">
          Network Not Connected
        </h2>
        <p className="text-secondary-600 text-center mb-6 max-w-md">
          You need to be connected to the P2P network to start chatting. Go to your Avatar page to connect first.
        </p>
        <button
          onClick={() => navigate('/avatar')}
          className="btn btn-primary"
        >
          Connect to Network
        </button>
      </div>
    )
  }

  if (connectedPeers.length === 0) {
    return (
      <div className="p-6 flex flex-col items-center justify-center h-full">
        <Users className="w-16 h-16 text-secondary-300 mb-4" />
        <h2 className="text-xl font-semibold text-secondary-900 mb-2">
          No Connected Peers
        </h2>
        <p className="text-secondary-600 text-center mb-6 max-w-md">
          You need to connect to other peers before you can start chatting. Add connections first.
        </p>
        <button
          onClick={() => navigate('/connections')}
          className="btn btn-primary"
        >
          Manage Connections
        </button>
      </div>
    )
  }

  return (
    <>
      <div className="h-full flex overflow-hidden">
        {/* Sidebar - Peer List */}
        <div className={`${selectedPeer ? 'hidden md:block' : 'block'} w-full md:w-80 bg-white flex flex-col min-h-0 ${selectedPeer ? 'md:border-r md:border-secondary-200' : ''}`}>
          {/* Header */}
          <div className="p-4 md:border-b md:border-secondary-200 flex-shrink-0">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-xl font-semibold text-secondary-900">
                Chat
              </h1>
              <button
                onClick={() => navigate('/connections')}
                className="p-2 hover:bg-secondary-100 rounded-lg"
                title="Manage Connections"
              >
                <Settings className="w-5 h-5 text-secondary-600" />
              </button>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-secondary-400" />
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 text-sm md:border md:border-secondary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 md:focus:border-primary-500"
              />
            </div>
          </div>

          {/* Peer List */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {filteredPeers.length === 0 ? (
              <div className="p-4 text-center">
                <MessageCircle className="w-12 h-12 text-secondary-300 mx-auto mb-3" />
                <p className="text-secondary-600">
                  {searchTerm ? 'No peers found' : 'No connected peers'}
                </p>
              </div>
            ) : (
              <div className="space-y-1 p-2">
                {filteredPeers.map((peer) => {
                  const peerMessages = messages.filter(msg =>
                    msg.senderId === peer.id || msg.recipients.includes(peer.id)
                  )
                  const lastMessage = peerMessages[peerMessages.length - 1]
                  const unreadCount = peerMessages.filter(msg =>
                    msg.senderId === peer.id && !msg.isSelf
                  ).length

                  return (
                    <button
                      key={peer.id}
                      onClick={() => handlePeerSelect(peer.id)}
                      className={`w-full p-3 rounded-lg text-left hover:bg-secondary-50 transition-colors ${
                        selectedPeer === peer.id ? 'bg-primary-50 md:border md:border-primary-200' : ''
                      }`}
                    >
                      <div className="flex items-center space-x-3 min-w-0">
                        <div className="relative flex-shrink-0">
                          <div className="w-12 h-12 bg-secondary-200 rounded-full flex items-center justify-center overflow-hidden">
                            {peer.selfta?.profileImage ? (
                              <img
                                src={peer.selfta.profileImage}
                                alt={`${peer.selfta.displayName || peer.selfta.alias}'s avatar`}
                                className="w-full h-full object-contain"
                              />
                            ) : (
                              <span className="text-secondary-600 font-medium">
                                {(peer.displayName || peer.alias || 'U').charAt(0).toUpperCase()}
                              </span>
                            )}
                          </div>
                          {peer.connectionState === 'connected' && (
                            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-success-500 rounded-full md:border-2 md:border-white" />
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-secondary-900 truncate">
                              {peer.displayName || peer.alias}
                            </span>
                            {lastMessage && (
                              <span className="text-xs text-secondary-500 flex-shrink-0 ml-2">
                                {formatMessageTime(lastMessage.timestamp)}
                              </span>
                            )}
                          </div>

                          <div className="flex items-center justify-between">
                            <p className="text-sm text-secondary-600 truncate flex-1">
                              {peer.isTyping ? (
                                <span className="text-primary-600 animate-pulse italic">typing...</span>
                              ) : lastMessage ? (
                                <span className={`flex items-center space-x-1 ${lastMessage.isSelf ? 'text-secondary-500' : ''}`}>
                                  {lastMessage.isSelf && <Check className="w-3 h-3 flex-shrink-0" />}
                                  <span className="truncate">{lastMessage.content}</span>
                                </span>
                              ) : (
                                <span className="text-secondary-400 italic">No messages yet</span>
                              )}
                            </p>
                            {unreadCount > 0 && (
                              <div className="flex-shrink-0 w-5 h-5 bg-primary-500 text-white text-xs rounded-full flex items-center justify-center ml-2">
                                {unreadCount > 99 ? '99+' : unreadCount}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className={`${!selectedPeer ? 'hidden md:block' : 'block'} flex-1 flex flex-col bg-secondary-50 min-h-0 min-w-0`}>
          {!selectedPeer ? (
            /* No chat selected */
            <div className="flex-1 flex flex-col items-center justify-center p-6">
              <MessageCircle className="w-16 h-16 text-secondary-300 mb-4" />
              <h2 className="text-xl font-semibold text-secondary-900 mb-2">
                Select a conversation
              </h2>
              <p className="text-secondary-600 text-center max-w-sm">
                Choose a peer from the sidebar to start chatting with them.
              </p>
            </div>
          ) : (
            <>
              {/* Chat Header */}
              <div className="bg-white md:border-b md:border-secondary-200 p-4 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => setSelectedPeer(null)}
                      className="md:hidden p-1 hover:bg-secondary-100 rounded-lg"
                    >
                      <ArrowLeft className="w-5 h-5 text-secondary-600" />
                    </button>

                    <div className="flex items-center space-x-3">
                      <div className="relative">
                        <div className="w-10 h-10 bg-secondary-200 rounded-full flex items-center justify-center overflow-hidden">
                          {selectedPeerData?.selfta?.profileImage ? (
                            <img
                              src={selectedPeerData.selfta.profileImage}
                              alt={`${selectedPeerData.selfta.displayName || selectedPeerData.selfta.alias}'s avatar`}
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <span className="text-secondary-600 font-medium">
                              {(selectedPeerData?.displayName || selectedPeerData?.alias || 'U').charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        {selectedPeerData?.connectionState === 'connected' && (
                          <Wifi className="absolute -bottom-1 -right-1 w-4 h-4 text-success-500" />
                        )}
                      </div>

                      <div>
                        <h2 className="font-semibold text-secondary-900">
                          {/* CRITICAL FIX: Proper alias resolution for enhanced chat header */}
                          {selectedPeerData?.selfta?.displayName || selectedPeerData?.displayName || selectedPeerData?.selfta?.alias || selectedPeerData?.alias || `User_${selectedPeerData?.id?.slice(-8) || 'Unknown'}`}
                        </h2>
                        <p className="text-sm text-secondary-600">
                          {selectedPeerData?.connectionState === 'connected' ? (
                            getTypingText() || 'Online'
                          ) : (
                            'Offline'
                          )}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    {/* DESKTOP ENHANCEMENT: Use enhanced call button for better desktop experience */}
                    <DesktopCallButton
                      targetPeerId={selectedPeerData?.id || ''}
                      targetAlias={selectedPeerData?.selfta?.alias || selectedPeerData?.alias || `User_${selectedPeerData?.id?.slice(-8) || 'Unknown'}`}
                      mediaService={mediaService}
                      onCallInitiated={(callType) => {
                        console.log(`📞 Desktop call initiated: ${callType}`)
                        // Call state will be updated by MediaService events
                      }}
                      isConnected={selectedPeerData?.connectionState === 'connected' && !callState.isInCall}
                      className="hidden sm:block" // Only show on desktop
                    />

                    {/* Fallback mobile buttons */}
                    <div className="flex items-center space-x-2 sm:hidden">
                      <button
                        onClick={handleVoiceCall}
                        disabled={selectedPeerData?.connectionState !== 'connected' || callState.isInCall}
                        className="p-2 hover:bg-secondary-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Voice Call"
                      >
                        <Phone className="w-5 h-5 text-secondary-600" />
                      </button>
                      <button
                        onClick={handleVideoCall}
                        disabled={selectedPeerData?.connectionState !== 'connected' || callState.isInCall}
                        className="p-2 hover:bg-secondary-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Video Call"
                      >
                        <Video className="w-5 h-5 text-secondary-600" />
                      </button>
                    </div>
                    <button
                      onClick={handleToggleScreen}
                      disabled={selectedPeerData?.connectionState !== 'connected' || !callState.isInCall}
                      className="p-2 hover:bg-secondary-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Share Screen"
                    >
                      <Monitor className="w-5 h-5 text-secondary-600" />
                    </button>
                    <div className="relative" data-dropdown="more-options">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setShowMoreDropdown(!showMoreDropdown)
                        }}
                        className="p-2 hover:bg-secondary-100 rounded-lg"
                        title="More Options"
                      >
                        <MoreVertical className="w-5 h-5 text-secondary-600" />
                      </button>

                      {showMoreDropdown && (
                        <div className="absolute right-0 top-full mt-2 w-48 bg-white shadow-lg rounded-lg md:border md:border-secondary-200 z-[100] min-w-max">
                          <div className="py-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleClearChat()
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2 cursor-pointer"
                            >
                              <Trash2 className="w-4 h-4 flex-shrink-0" />
                              <span>Clear Chat History</span>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Active call indicator */}
                {callState.isInCall && callState.activePeer === selectedPeer && (
                  <div className="mt-3 p-2 bg-primary-50 md:border md:border-primary-200 rounded-lg flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                      <span className="text-sm text-primary-700">
                        {callState.callType === 'video' ? 'Video' : 'Voice'} call in progress
                      </span>
                    </div>
                    <button
                      onClick={handleEndCall}
                      className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm rounded-lg flex items-center space-x-1"
                    >
                      <PhoneOff className="w-4 h-4" />
                      <span>End</span>
                    </button>
                  </div>
                )}
              </div>

              {/* Messages Container */}
              <div className="flex-1 flex flex-col min-h-0">
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {filteredMessages.length === 0 ? (
                    <div className="text-center py-12">
                      <MessageCircle className="w-12 h-12 text-secondary-300 mx-auto mb-3" />
                      <p className="text-secondary-600">
                        No messages yet. Send the first message!
                      </p>
                    </div>
                  ) : (
                    <>
                      {filteredMessages.map((message) => (
                        <div
                          key={message.id}
                          className={`flex ${message.isSelf ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-xs lg:max-w-md px-3 py-2 rounded-2xl shadow-sm ${
                              message.isSelf
                                ? 'bg-primary-500 text-white ml-auto'
                                : 'bg-white text-secondary-900 md:border md:border-secondary-200'
                            }`}
                          >
                            {/* Render message content based on type */}
                            {message.type === 'file' && message.fileData ? (
                              <div className="space-y-2">
                                <p className="break-words">{message.content}</p>
                                {!message.isSelf && (
                                  <button
                                    onClick={() => handleFileDownload(message.fileData!)}
                                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors mobile-touch-target touch-manipulation min-h-[44px] mobile-file-download-btn ${
                                      message.isSelf
                                        ? 'bg-primary-600 hover:bg-primary-700 text-white'
                                        : 'bg-secondary-100 hover:bg-secondary-200 text-secondary-700'
                                    }`}
                                    aria-label={`Download ${message.fileData?.name || 'file'}`}
                                    type="button"
                                  >
                                    <Download className="w-4 h-4 flex-shrink-0 download-icon" />
                                    <span className="text-sm download-text">Download</span>
                                  </button>
                                )}
                              </div>
                            ) : message.type === 'location' && message.locationData ? (
                              <LocationMessage
                                location={message.locationData}
                                shareType={message.locationData.shareType}
                                duration={message.locationData.duration || undefined}
                                address={message.locationData.address}
                                fromSelf={message.isSelf}
                              />
                            ) : (
                              <p className="break-words">{message.content}</p>
                            )}

                            <div className={`flex items-center justify-end mt-1 text-xs ${
                              message.isSelf ? 'text-primary-100' : 'text-secondary-500'
                            }`}>
                              <span>{formatMessageTime(message.timestamp)}</span>
                              {message.isSelf && (
                                <CheckCheck className="w-3 h-3 ml-1" />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Typing indicator */}
                      {getTypingText() && (
                        <div className="flex justify-start mb-4">
                          <div className="bg-white text-secondary-600 px-4 py-3 rounded-2xl shadow-sm md:border md:border-secondary-200">
                            <div className="flex items-center space-x-2">
                              <div className="flex space-x-1">
                                <div className="w-2 h-2 bg-secondary-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                <div className="w-2 h-2 bg-secondary-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                <div className="w-2 h-2 bg-secondary-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                              </div>
                              <span className="text-xs text-secondary-500">{getTypingText()}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </div>

              {/* Message Input */}
              <div className="bg-white md:border-t md:border-secondary-200 p-3 md:p-4 flex-shrink-0">
                {selectedPeerData?.connectionState !== 'connected' && (
                  <div className="mb-3 flex items-center space-x-2 p-2 bg-warning-50 md:border md:border-warning-200 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-warning-600" />
                    <p className="text-sm text-warning-700">
                      Peer is offline. Messages may not be delivered.
                    </p>
                  </div>
                )}

                <div className="flex items-end space-x-2 md:space-x-3">
                  <div className="flex-1 relative min-w-0">
                    <div className="flex items-center bg-secondary-100 rounded-full px-3 md:px-4 py-2 min-h-[44px] overflow-hidden">
                      <input
                        ref={messageInputRef}
                        type="text"
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Type a message..."
                        className="flex-1 bg-transparent border-0 outline-none resize-none placeholder-secondary-500 text-secondary-900"
                        disabled={selectedPeerData?.connectionState !== 'connected'}
                      />

                      <div className="flex items-center space-x-0.5 md:space-x-1 ml-1 md:ml-2">
                        <button
                          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                          className="p-1.5 hover:bg-secondary-200 rounded-full transition-colors"
                          type="button"
                        >
                          <Smile className="w-5 h-5 text-secondary-600" />
                        </button>

                        <button
                          onClick={() => setShowFileTransfer(true)}
                          className="p-1.5 hover:bg-secondary-200 rounded-full transition-colors"
                          type="button"
                        >
                          <Paperclip className="w-5 h-5 text-secondary-600" />
                        </button>

                        {selectedPeerForChat && (
                          <LocationSharingButton
                            peerId={selectedPeerForChat}
                            peerAlias={connections.get(selectedPeerForChat)?.alias || 'Unknown'}
                            onLocationShare={handleLocationShare}
                            isSharing={locationService.isSharingWithPeer(selectedPeerForChat)}
                          />
                        )}
                      </div>
                    </div>

                    {/* Emoji Picker */}
                    {showEmojiPicker && (
                      <EmojiPicker
                        isOpen={showEmojiPicker}
                        onEmojiSelect={handleEmojiSelect}
                        onClose={() => setShowEmojiPicker(false)}
                        position="top"
                      />
                    )}
                  </div>

                  <button
                    onClick={handleSendMessage}
                    disabled={!messageText.trim() || selectedPeerData?.connectionState !== 'connected'}
                    className="p-3 md:p-4 w-[44px] h-[44px] md:min-w-[44px] md:min-h-[44px] bg-primary-500 text-white rounded-full hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0 flex items-center justify-center touch-manipulation"
                    aria-label="Send message"
                    title="Send message"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* File Transfer Modal */}
      <FileTransferUI
        isOpen={showFileTransfer}
        onClose={() => setShowFileTransfer(false)}
        onFileSelect={handleFileSelect}
        activeTransfers={activeTransfers}
        maxFileSize={100}
        multiple={true}
      />

      {/* Voice-First Architecture: Call UI for both voice and video calls */}
      {callState.isInCall && (callState.callType === 'video' || callState.callType === 'voice') && (
        <VideoCallUI
          callState={callState}
          localStream={callState.localStream}
          remoteStream={callState.remoteStream}
          onEndCall={handleEndCall}
          onToggleMute={handleToggleMute}
          onToggleVideo={handleToggleVideo}
          onToggleScreen={handleToggleScreen}
          onUpgradeToVideo={handleUpgradeToVideo}
          isMuted={isMuted}
          isVideoEnabled={isVideoEnabled}
          isScreenSharing={isScreenSharing}
          connectionQuality="good"
          videoQuality={videoQuality}
          isDegraded={isDegraded}
        />
      )}

      {/* IncomingCallNotification moved to MainLayout for global visibility */}

      {/* Mobile Audio Guidance */}
      <MobileAudioGuidance
        isVisible={showMobileAudioGuidance}
        onDismiss={() => setShowMobileAudioGuidance(false)}
        callType={callState.callType || 'voice'}
      />
    </>
  )
}